import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, BadgeCheck, CalendarDays, PlaneLanding, PlaneTakeoff, Plane, Radio } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const heroImg = "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1900&q=85";
const apronImg = "https://images.unsplash.com/photo-1540339832862-474599807836?auto=format&fit=crop&w=1400&q=85";

const pt = {
  en: {
    eyebrow: "LIVE OPERATIONS — PATNA AIRPORT (PAT)",
    h1a: "Patna Airport",
    h1b: "Traffic Intelligence.",
    heroCopy: "We manage parking and ground traffic at Patna Airport every single day. Pick a date and see how many flights came in and went out.",
    coming: "Flights coming", going: "Flights going", total: "Total today",
    marquee: "PATNA AIRPORT — DAILY TRAFFIC — PARKING MANAGEMENT — FLIGHTS IN — FLIGHTS OUT — ",
    ch1num: "01", ch1title: "We manage the movement.", ch1text: "Sanjivani Ellite Ventures manages Patna Airport's parking and kerbside traffic on a daily basis. Bays, barriers, staff and passenger flow — coordinated so every arrival and departure keeps moving.",
    ch2num: "02", ch2title: "Pick a date. See the flights.", ch2text: "Choose any day to see how many flights arrived at and departed from Patna Airport.",
    dateLabel: "Choose date", flightsIn: "Flights IN", flightsOut: "Flights OUT", totalFlights: "Total flights", updated: "Data updated",
    sourceLive: "Live flight data", sourceOfficial: "Official AAI daily report", sourceEst: "Estimated — live feed coming", sourceRecorded: "Recorded daily total (from live feed)",
    sourceUnavail: "No verified record for this date", noRecord: "Verified flight numbers for this date are not in our records yet. Share the official AAI daily report for this day and we will pin it here.",
    sourceFuture: "Schedule not published yet", futureNote: "The live schedule for this day has not been published by the flight feed yet. Check back closer to the date — schedules usually appear 1 to 2 days ahead.",
    arrivalsTitle: "Arrivals", departuresTitle: "Departures", noList: "Detailed flight list is available for days with live data.", colFlight: "Flight", colRoute: "Route", colTime: "Time (IST)", colStatus: "Status",
    officialTitle: "Official AAI daily report", officialDate: "19 August 2026",
    officialNote: "Published by Airports Authority of India, Jay Prakash Narayan International Airport, Patna (@aaipatairport).",
    ch3num: "03", ch3title: "Upcoming traffic, day by day.", ch3text: "A seven-day outlook from your chosen date — flights coming in, flights going out, and the daily total.",
    ch4num: "04", ch4title: "The month at a glance.", ch4text: "Every recorded day in one calendar — official reports, live days and recorded totals. Tap any day with numbers to open it above.",
    prevMonth: "Previous month", nextMonth: "Next month",
    thDate: "Date", thDay: "Day", thIn: "Flights IN", thOut: "Flights OUT", thTotal: "Total",
    selected: "Your date",
    disclaimer: "Green = live flight feed · blue = official AAI report · dark = recorded daily total. No estimated numbers are shown anywhere on this page.",
    cta: "Talk to our operations team",
    fr24: "View live radar on Flightradar24",
    days: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  },
  hi: {
    eyebrow: "लाइव ऑपरेशंस — पटना एयरपोर्ट (PAT)",
    h1a: "पटना एयरपोर्ट",
    h1b: "ट्रैफिक इंटेलिजेंस।",
    heroCopy: "हम पटना एयरपोर्ट पर रोज़ाना पार्किंग और ग्राउंड ट्रैफिक संभालते हैं। तारीख चुनें और देखें कितनी फ्लाइटें आईं और कितनी गईं।",
    coming: "आने वाली फ्लाइटें", going: "जाने वाली फ्लाइटें", total: "आज कुल",
    marquee: "पटना एयरपोर्ट — दैनिक ट्रैफिक — पार्किंग प्रबंधन — फ्लाइट इन — फ्लाइट आउट — ",
    ch1num: "01", ch1title: "हम गतिविधि संभालते हैं।", ch1text: "संजिवनी एलीट वेंचर्स पटना एयरपोर्ट की पार्किंग और कर्बसाइड ट्रैफिक का दैनिक प्रबंधन करता है। बे, बैरियर, स्टाफ और यात्री प्रवाह — समन्वित, ताकि हर आगमन और प्रस्थान चलता रहे।",
    ch2num: "02", ch2title: "तारीख चुनें। फ्लाइटें देखें।", ch2text: "कोई भी दिन चुनें और देखें कि पटना एयरपोर्ट पर कितनी फ्लाइटें आईं और कितनी रवाना हुईं।",
    dateLabel: "तारीख चुनें", flightsIn: "फ्लाइट IN", flightsOut: "फ्लाइट OUT", totalFlights: "कुल फ्लाइटें", updated: "डेटा अपडेट",
    sourceLive: "लाइव फ्लाइट डेटा", sourceOfficial: "आधिकारिक AAI दैनिक रिपोर्ट", sourceEst: "अनुमानित — लाइव फीड जल्द आ रही है", sourceRecorded: "दर्ज दैनिक कुल (लाइव फीड से)",
    sourceUnavail: "इस तारीख का कोई सत्यापित रिकॉर्ड नहीं", noRecord: "इस तारीख के सत्यापित फ्लाइट आंकड़े अभी हमारे रिकॉर्ड में नहीं हैं। इस दिन की आधिकारिक AAI दैनिक रिपोर्ट साझा करें और हम उसे यहाँ दर्ज कर देंगे।",
    sourceFuture: "शेड्यूल अभी प्रकाशित नहीं", futureNote: "इस दिन का लाइव शेड्यूल फ्लाइट फीड में अभी प्रकाशित नहीं हुआ है। तारीख के करीब फिर देखें — शेड्यूल आमतौर पर 1 से 2 दिन पहले आता है।",
    arrivalsTitle: "आगमन", departuresTitle: "प्रस्थान", noList: "विस्तृत फ्लाइट सूची लाइव डेटा वाले दिनों के लिए उपलब्ध है।", colFlight: "फ्लाइट", colRoute: "मार्ग", colTime: "समय (IST)", colStatus: "स्थिति",
    officialTitle: "आधिकारिक AAI दैनिक रिपोर्ट", officialDate: "19 अगस्त 2026",
    officialNote: "भारतीय विमानपत्तन प्राधिकरण, जय प्रकाश नारायण अंतरराष्ट्रीय हवाई अड्डा, पटना (@aaipatairport) द्वारा प्रकाशित।",
    ch3num: "03", ch3title: "आगामी ट्रैफिक, दिन-प्रतिदिन।", ch3text: "आपकी चुनी तारीख से सात दिन का पूर्वानुमान — आने वाली फ्लाइटें, जाने वाली फ्लाइटें और दैनिक कुल।",
    ch4num: "04", ch4title: "पूरा महीना एक नज़र में।", ch4text: "हर दर्ज दिन एक कैलेंडर में — आधिकारिक रिपोर्ट, लाइव दिन और दर्ज कुल। जिस दिन आंकड़े हैं उसे टैप करें और ऊपर खोलें।",
    prevMonth: "पिछला महीना", nextMonth: "अगला महीना",
    thDate: "तारीख", thDay: "दिन", thIn: "फ्लाइट IN", thOut: "फ्लाइट OUT", thTotal: "कुल",
    selected: "आपकी तारीख",
    disclaimer: "हरा = लाइव फ्लाइट फीड · नीला = आधिकारिक AAI रिपोर्ट · गहरा हरा = दर्ज दैनिक कुल। इस पेज पर कहीं भी अनुमानित आंकड़े नहीं दिखाए जाते।",
    cta: "हमारी ऑपरेशंस टीम से बात करें",
    fr24: "Flightradar24 पर लाइव रडार देखें",
    days: ["रवि", "सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि"],
  },
};

const iso = (d) => d.toISOString().slice(0, 10);
const fmtDate = (d, lang) => d.toLocaleDateString(lang === "en" ? "en-IN" : "hi-IN", { day: "numeric", month: "short", year: "numeric" });

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 44 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>{children}</motion.div>
);

const FlightColumn = ({ title, icon: Icon, flights, t, testid }) => (
  <div className="pt-flights-col" data-testid={testid}>
    <h3><Icon size={18} />{title} <span>({flights.length})</span></h3>
    <div className="pt-flights-head"><span>{t.colFlight}</span><span>{t.colRoute}</span><span>{t.colTime}</span></div>
    {flights.map((f, i) => (
      <div className="pt-flight-row" key={`${f.flight}-${i}`} data-testid={`${testid}-row-${i}`}>
        <span className="pt-flight-no">{f.flight}</span>
        <span className="pt-flight-route">{f.city}<small>{f.airline}</small></span>
        <span className="pt-flight-time">{f.time}<small className={`pt-status pt-status-${f.status}`}>{f.status}</small></span>
      </div>
    ))}
  </div>
);

export default function PatnaTraffic({ lang }) {
  const t = pt[lang];
  const [dateStr, setDateStr] = useState(iso(new Date()));
  const [todayCounts, setTodayCounts] = useState(null);
  const [dayCounts, setDayCounts] = useState(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15 });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    axios.get(`${API}/patna-flights`).then((r) => setTodayCounts(r.data)).catch(() => setTodayCounts(null));
  }, []);

  useEffect(() => {
    setDayCounts(null);
    axios.get(`${API}/patna-flights`, { params: { date: dateStr } }).then((r) => setDayCounts(r.data)).catch(() => setDayCounts({ date: dateStr, in: null, out: null, total: null, source: "unavailable" }));
  }, [dateStr]);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);

  const selected = useMemo(() => new Date(dateStr + "T00:00:00"), [dateStr]);
  const outlook = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selected); d.setDate(d.getDate() + i);
    return { date: d };
  }), [selected]);

  const [outlookLive, setOutlookLive] = useState(null);
  useEffect(() => {
    setOutlookLive(null);
    Promise.all(outlook.map((r) => axios.get(`${API}/patna-flights`, { params: { date: iso(r.date) } }).then((res) => res.data).catch(() => null))).then(setOutlookLive);
  }, [outlook]);

  const [monthStr, setMonthStr] = useState(iso(new Date()).slice(0, 7));
  const [monthDays, setMonthDays] = useState({});
  useEffect(() => {
    axios.get(`${API}/patna-flights/month`, { params: { month: monthStr } }).then((r) => setMonthDays(r.data.days || {})).catch(() => setMonthDays({}));
  }, [monthStr]);
  const monthDate = new Date(monthStr + "-01T00:00:00");
  const shiftMonth = (n) => { const d = new Date(monthDate); d.setMonth(d.getMonth() + n); setMonthStr(iso(d).slice(0, 7)); };
  const monthCells = useMemo(() => {
    const first = new Date(monthDate); const offset = first.getDay();
    const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    return [...Array(offset).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  }, [monthStr]);

  const hero = todayCounts || { in: null, out: null, total: null };
  const counts = dayCounts || { in: null, out: null, total: null };
  const sourceLabel = dayCounts?.source === "live" ? t.sourceLive : dayCounts?.source === "official" ? t.sourceOfficial : dayCounts?.source === "recorded" ? t.sourceRecorded : dayCounts?.detail === "future" ? t.sourceFuture : t.sourceUnavail;
  const lines = [t.h1a, t.h1b];

  return (
    <div className="inner-page pt-page">
      <section className="pt-hero" ref={heroRef}>
        <motion.img src={heroImg} alt="Aircraft wing above the clouds" className="pt-hero-img" style={{ y: imgY }} />
        <div className="pt-hero-shade" />
        <div className="container pt-hero-content">
          <motion.p className="eyebrow light" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }}>{t.eyebrow}</motion.p>
          <h1 data-testid="pt-hero-title">
            {lines.map((line, i) => (
              <span className="pt-mask" key={line}>
                <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.05, delay: 0.15 + i * 0.16, ease: [0.22, 1, 0.36, 1] }}>{line}</motion.span>
              </span>
            ))}
          </h1>
          <motion.p className="pt-hero-copy" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.9 }}>{t.heroCopy}</motion.p>
          <motion.div className="pt-stats" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9 }}>
            <div data-testid="pt-flights-in"><PlaneLanding size={20} /><div><strong>{hero.in ?? "—"}</strong><span>{t.coming}</span></div></div>
            <div data-testid="pt-flights-out"><PlaneTakeoff size={20} /><div><strong>{hero.out ?? "—"}</strong><span>{t.going}</span></div></div>
            <div data-testid="pt-flights-total"><Plane size={20} /><div><strong>{hero.total ?? "—"}</strong><span>{t.total}</span></div></div>
          </motion.div>
        </div>
      </section>

      <div className="pt-marquee" aria-hidden="true"><div className="pt-marquee-track"><span>{t.marquee.repeat(4)}</span><span>{t.marquee.repeat(4)}</span></div></div>

      <section className="section pt-chapter">
        <div className="container pt-chapter-grid">
          <Reveal><span className="pt-num">{t.ch1num}</span></Reveal>
          <Reveal delay={0.1}>
            <h2 data-testid="pt-ch1-title">{t.ch1title}</h2>
            <p className="lead">{t.ch1text}</p>
          </Reveal>
          <Reveal delay={0.2} className="pt-chapter-img"><img src={apronImg} alt="Airport apron operations" /></Reveal>
        </div>
      </section>

      <section className="section pt-planner sand-section">
        <div className="container">
          <div className="pt-chapter-grid">
            <Reveal><span className="pt-num">{t.ch2num}</span></Reveal>
            <Reveal delay={0.1}><h2>{t.ch2title}</h2><p className="lead">{t.ch2text}</p></Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="pt-date-bar">
              <label data-testid="pt-date-label"><CalendarDays size={18} />{t.dateLabel}
                <input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="pt-date-input" />
              </label>
              <span className={`pt-source pt-source-${dayCounts?.source || "estimated"}`} data-testid="pt-source-badge"><Radio size={13} />{sourceLabel}</span>
              {dayCounts?.fetched_at && <span className="pt-updated" data-testid="pt-updated-at">{t.updated}: {new Date(dayCounts.fetched_at).toLocaleString(lang === "en" ? "en-IN" : "hi-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} IST</span>}
            </div>
          </Reveal>
          <div className="pt-result-cards">
            {[
              { icon: PlaneLanding, label: t.flightsIn, value: counts.in, tid: "pt-day-in" },
              { icon: PlaneTakeoff, label: t.flightsOut, value: counts.out, tid: "pt-day-out" },
              { icon: Plane, label: t.totalFlights, value: counts.total, tid: "pt-day-total" },
            ].map((c, i) => (
              <Reveal key={c.tid} delay={0.1 + i * 0.08}>
                <div className="pt-card" data-testid={c.tid}><c.icon size={22} /><span>{c.label}</span><strong>{c.value ?? "—"}</strong></div>
              </Reveal>
            ))}
          </div>
          {dayCounts?.source === "unavailable" && <Reveal delay={0.12}><p className="pt-norecord" data-testid="pt-no-record">{dayCounts?.detail === "future" ? t.futureNote : t.noRecord}</p></Reveal>}
          {dayCounts?.flights ? (
            <Reveal delay={0.18}>
              <div className="pt-flights" data-testid="pt-flight-list">
                <FlightColumn title={t.arrivalsTitle} icon={PlaneLanding} flights={dayCounts.flights.in} t={t} testid="pt-arrivals-list" />
                <FlightColumn title={t.departuresTitle} icon={PlaneTakeoff} flights={dayCounts.flights.out} t={t} testid="pt-departures-list" />
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.18}><p className="rep-note" data-testid="pt-no-list">{t.noList}</p></Reveal>
          )}
          <Reveal delay={0.2}>
            <div className="pt-official" data-testid="pt-official-card">
              <div className="pt-official-head"><BadgeCheck size={20} /><div><strong>{t.officialTitle}</strong><span>{t.officialDate}</span></div></div>
              <div className="pt-official-nums">
                <div><PlaneLanding size={16} /><strong>33</strong><span>{t.flightsIn}</span></div>
                <div><PlaneTakeoff size={16} /><strong>34</strong><span>{t.flightsOut}</span></div>
                <div><Plane size={16} /><strong>67</strong><span>{t.totalFlights}</span></div>
              </div>
              <p>{t.officialNote}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="pt-chapter-grid">
            <Reveal><span className="pt-num">{t.ch3num}</span></Reveal>
            <Reveal delay={0.1}><h2>{t.ch3title}</h2><p className="lead">{t.ch3text}</p></Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="pt-table-wrap">
              <table className="pt-table" data-testid="pt-traffic-table">
                <thead><tr><th>{t.thDate}</th><th>{t.thDay}</th><th>{t.thIn}</th><th>{t.thOut}</th><th>{t.thTotal}</th></tr></thead>
                <tbody>
                  {outlook.map((row, i) => {
                    const api = outlookLive?.[i];
                    const data = api?.source === "unavailable" ? null : (api || row);
                    return (
                      <tr key={iso(row.date)} className={i === 0 ? "pt-row-selected" : ""} data-testid={`pt-row-${i}`}>
                        <td>{fmtDate(row.date, lang)}{i === 0 && <em className="pt-tag">{t.selected}</em>}</td>
                        <td>{t.days[row.date.getDay()]}</td>
                        <td>{data ? <span className="pt-in"><PlaneLanding size={14} />{data.in}</span> : "—"}</td>
                        <td>{data ? <span className="pt-out"><PlaneTakeoff size={14} />{data.out}</span> : "—"}</td>
                        <td>{data ? <><strong>{data.total}</strong>{api && <i className={`pt-dot pt-dot-${api.source}`} title={api.source} />}</> : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal delay={0.2}><p className="rep-note" data-testid="pt-disclaimer">{t.disclaimer}</p></Reveal>
          <Reveal delay={0.25}>
            <div className="pt-actions">
              <NavLink to="/contact" className="button" data-testid="pt-contact-cta">{t.cta}<ArrowUpRight size={17} /></NavLink>
              <a href="https://www.flightradar24.com/data/airports/pat/arrivals" target="_blank" rel="noopener noreferrer" className="button button-light pt-fr24" data-testid="pt-fr24-link"><Radio size={16} />{t.fr24}<ArrowUpRight size={17} /></a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section pt-archive sand-section">
        <div className="container">
          <div className="pt-chapter-grid">
            <Reveal><span className="pt-num">{t.ch4num}</span></Reveal>
            <Reveal delay={0.1}><h2>{t.ch4title}</h2><p className="lead">{t.ch4text}</p></Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="pt-cal" data-testid="pt-calendar">
              <div className="pt-cal-nav">
                <button onClick={() => shiftMonth(-1)} data-testid="pt-prev-month">← {t.prevMonth}</button>
                <strong data-testid="pt-month-title">{monthDate.toLocaleDateString(lang === "en" ? "en-IN" : "hi-IN", { month: "long", year: "numeric" })}</strong>
                <button onClick={() => shiftMonth(1)} data-testid="pt-next-month">{t.nextMonth} →</button>
              </div>
              <div className="pt-cal-grid">
                {t.days.map((d) => <span key={d} className="pt-cal-dow">{d}</span>)}
                {monthCells.map((day, i) => {
                  if (!day) return <span key={`x${i}`} className="pt-cal-cell pt-cal-empty" />;
                  const dIso = `${monthStr}-${String(day).padStart(2, "0")}`;
                  const info = monthDays[dIso];
                  return (
                    <button key={dIso} className={`pt-cal-cell ${info ? `pt-cal-${info.source}` : ""}`} onClick={() => info && setDateStr(dIso)} disabled={!info} data-testid={`pt-cal-day-${day}`}>
                      <span className="pt-cal-num">{day}</span>
                      {info && <span className="pt-cal-total">{info.total}<small>{info.in}↑ {info.out}↓</small></span>}
                    </button>
                  );
                })}
              </div>
              <div className="pt-cal-legend">
                <span><i className="pt-dot pt-dot-official" />{t.sourceOfficial}</span>
                <span><i className="pt-dot pt-dot-live" />{t.sourceLive}</span>
                <span><i className="pt-dot pt-dot-recorded" />{t.sourceRecorded}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
